// src/components/PlatformCoverageAoisMap.jsx
import React, { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import { whenMapLoaded } from "../../lib/map/whenMapLoaded";
import { attachAttribution, ESRI_CREDITS } from "../../lib/map/attribution";
import MlDraw from "../../lib/ml-draw/ml-draw.js";
import "maplibre-gl/dist/maplibre-gl.css";
import area from "@turf/area";
import { fetchWithAuth } from "../../api/fetchWithAuth";
import MapContextHeader from "./MapContextHeader";
import PlatformCoverageAoiToolbar from "./PlatformCoverageAoiToolbar";

const GRID_MODES = {
  NONE: "none",
  DH: "dh",
};

export default function PlatformCoverageAoisMap({
  aois,
  selectedId,
  createMode,
  meta,
  onCreated,
  onPolygonComplete,   // ← new: called when polygon is closed
  onDrawReset,         // ← new: called when draw is reset
  onSaveRequested,     // ← new: boolean, true triggers save
  onSaveDone,          // ← new: called after successful save
  onSaveError,         // ← new: called with error message on failure
}) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const drawRef = useRef(null);
  const [drawInstance, setDrawInstance] = useState(null);
  const [drawVersion, setDrawVersion] = useState(0);
  const [mapReady, setMapReady] = useState(false);
  const [pendingGeom, setPendingGeom] = useState(null);
  const [hasDraftAoi, setHasDraftAoi] = useState(false);
  const [geomCache, setGeomCache] = useState({});
  const [gridMode, setGridMode] = useState(GRID_MODES.NONE);
  const [snapEnabled, setSnapEnabled] = useState(false);
  const snapCandidateRef = useRef(null);
  const snapInflightRef = useRef(false);
  const snapLastAtRef = useRef(0);
  const snapAbortRef = useRef(null);
  const snapEnabledRef = useRef(false);
  const gridModeRef = useRef(GRID_MODES.NONE);

  function computeSqMi(feature) {
    if (!feature) return null;
    const m2 = area(feature);
    return m2 / 2.58998811e6;
  }

  function resetDraftAoi() {
    const d = drawRef.current;
    if (!d) return;
    d.stopDrawing?.();
    d.delete?.();
    d.lastFeature = null;
    clearSnapPreview();
    setPendingGeom(null);
    setHasDraftAoi(false);
    setDrawVersion(v => v + 1);
    onDrawReset?.();
  }

  function isAuthReady() {
    const kc = window.__keycloak;
    return Boolean(kc && kc.authenticated && kc.token);
  }

  // ---------------------------------------------------------------------
  // SNAP HANDLER
  // ---------------------------------------------------------------------
  const SNAP_TOLERANCE_M = 150;

  async function resolveSnap({ lng, lat }) {
    if (!snapEnabledRef.current) return null;
    if (gridModeRef.current !== GRID_MODES.DH) return null;
    if (!isAuthReady()) return null;

    // Abort previous request
    try { snapAbortRef.current?.abort(); } catch { }
    const ac = new AbortController();
    snapAbortRef.current = ac;

    const res = await fetchWithAuth("/api/v1/platform/us48-grid/snap/vertex", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ lng, lat, radius_m: SNAP_TOLERANCE_M }),
      signal: ac.signal,
    }).catch((e) => { console.error("[ui/src/platform/components/PlatformCoverageAoiMap.jsx] POST /api/v1/platform/us48-grid/snap/vertex failed (swallowed):", e); return null; });

    if (!res?.ok) return null;

    const data = await res.json().catch((e) => { console.error("[ui/src/platform/components/PlatformCoverageAoiMap.jsx] res.json() failed (swallowed):", e); return null; });
    if (!data?.snapped) return null;
    if (data.distance_m > SNAP_TOLERANCE_M) return null;

    return { type: "vertex", coords: data.snapped };
  }

  // ---------------------------------------------------------------------
  // SAVE HANDLER
  // ---------------------------------------------------------------------
  async function saveCoverageAoi() {
    if (!drawRef.current?.lastFeature) return;
    if (!meta?.name?.trim()) {
      onSaveError?.("Name is required");
      return;
    }

    try {
      const slug = meta.slug ||
        meta.name.trim().toLowerCase().replace(/\s+/g, "-");

      const res = await fetchWithAuth("/api/v1/platform/coverage-aois", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slug,
          name: meta.name.trim(),
          basin: meta.basin?.trim() || null,
          region: meta.region?.trim() || null,
          source: "platform",
          geom: drawRef.current.lastFeature,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error || "Save failed");

      resetDraftAoi();
      onSaveDone?.();
      onCreated?.(data.id);

    } catch (e) {
      console.error("Save coverage AOI failed", e);
      onSaveError?.(e.message || "Save failed");
    }
  }

  async function fetchAoiGeom(aoiId) {
    const res = await fetchWithAuth(`/api/v1/platform/coverage-aois/${aoiId}`);
    if (!res.ok) throw new Error("Failed to load AOI geometry");
    return await res.json();
  }

  useEffect(() => {
    if (hasDraftAoi) {
      onPolygonComplete?.();
    }
  }, [hasDraftAoi]);

  useEffect(() => {
    if (onSaveRequested) {
      saveCoverageAoi();
    }
  }, [onSaveRequested]);

  useEffect(() => {
    snapEnabledRef.current = snapEnabled;
  }, [snapEnabled]);

  useEffect(() => {
    gridModeRef.current = gridMode;
  }, [gridMode]);

  useEffect(() => {
    if (!createMode || !drawRef.current) return;
    drawRef.current.stopDrawing?.();
    drawRef.current.delete?.();
    drawRef.current.lastFeature = null;
    setHasDraftAoi(false);
    drawRef.current.startPolygon();
  }, [createMode]);

  function clearSnapPreview() {
    const map = mapRef.current;
    if (!map) return;

    const ptSrc = map.getSource("snap-preview");
    const lineSrc = map.getSource("snap-preview-line");

    if (ptSrc) {
      ptSrc.setData({ type: "FeatureCollection", features: [] });
      map.setLayoutProperty("snap-preview-point", "visibility", "none");
    }

    if (lineSrc) {
      lineSrc.setData({ type: "FeatureCollection", features: [] });
      map.setLayoutProperty("snap-preview-line", "visibility", "none");
    }

    snapCandidateRef.current = null;
  }

  // ---------------------------------------------------------------------
  // INITIALIZE MAP + DRAW ENGINE
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!mapContainerRef.current) return;

    //const map = new maplibregl.Map({
    //  container: mapContainerRef.current,
    //  style: `https://api.maptiler.com/maps/hybrid/style.json?key=${import.meta.env.VITE_MAPTILER_KEY}`,
    //  center: [-102, 32],
    //  zoom: 7,
    //  attributionControl: false,
    // });

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: {
        version: 8,
        sources: {
          "esri-world-imagery": {
            type: "raster",
            tiles: [
              "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            ],
            tileSize: 256,
            attribution: ESRI_CREDITS["World_Imagery"],
          },
          "esri-transportation": {
            type: "raster",
            tiles: [
              "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}"
            ],
            tileSize: 256,
            attribution: ESRI_CREDITS["Reference/World_Transportation"],
          },
          "esri-boundaries": {
            type: "raster",
            tiles: [
              "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
            ],
            tileSize: 256,
            attribution: ESRI_CREDITS["Reference/World_Boundaries_and_Places"],
          },
        },
        layers: [
          {
            id: "esri-world-imagery",
            type: "raster",
            source: "esri-world-imagery",
          },
          {
            id: "esri-transportation",
            type: "raster",
            source: "esri-transportation",
            paint: { "raster-opacity": 0.65 },
          },
          {
            id: "esri-boundaries",
            type: "raster",
            source: "esri-boundaries",
            paint: { "raster-opacity": 0.8 },
          },
        ],
      },
      center: [-102, 32],
      zoom: 7,
      attributionControl: { compact: false },
    });
    // Esri's credit and "Powered by Esri" (REM-684).
    attachAttribution(map);

    map.getCanvas().style.background = "#0C0E10";
    mapRef.current = map;

    const onLoad = () => {
      // INIT MlDraw
      const draw = new MlDraw();
      draw.onUpdate = () => setDrawVersion((v) => v + 1);

      map.addControl(draw);
      drawRef.current = draw;
      setDrawInstance(draw);

      // RIGHT-CLICK CLOSE POLYGON (SAFE: scoped to map canvas only)
      map.on("contextmenu", (e) => {
        const d = drawRef.current;
        if (!d || !d.drawing || d.mode !== "polygon") return;

        e.preventDefault(); // suppress browser menu

        const snap = snapCandidateRef.current;
        const finalPoint = snap?.coords
          ? snap.coords
          : [e.lngLat.lng, e.lngLat.lat];

        d.completePolygon?.(finalPoint);

        snapCandidateRef.current = null;
        clearSnapPreview();
      });

      // THE GRID THE SALE IS COMPUTED ON.
      //
      // Was reference.dh_grid_cells. A coverage AOI is resolved to
      // aiprime.us48_grid cells when the entitlement is issued, so drawing
      // against dh_grid let an admin snap precisely to a lattice nobody bills
      // on -- the two grids share no vertices, and "snapped exactly" was never
      // "paid for exactly".
      //
      // minzoom 10 matches the route's floor: one-mile cells are not a usable
      // snap target below it, and the national grid is 9M cells with no
      // coverage join to thin it.
      map.addSource("dh-grid", {
        type: "vector",
        tiles: [
          `/api/v1/platform/us48-grid/tiles/{z}/{x}/{y}.pbf`
        ],
        minzoom: 10,
        maxzoom: 20,
      });

      map.addLayer({
        id: "dh-grid-outline",
        type: "line",
        source: "dh-grid",
        "source-layer": "us48",
        paint: {
          "line-color": "#ffffff",
          "line-width": 1,
          "line-opacity": 0.4,
        },
      });
      map.setLayoutProperty("dh-grid-outline", "visibility", "none");

      map.addSource("snap-preview", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });

      map.addLayer({
        id: "snap-preview-point",
        type: "circle",
        source: "snap-preview",
        paint: {
          "circle-radius": 4,
          "circle-color": "#16f2d4",
          "circle-opacity": 0.85,
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 2.5,
        },
      });
      map.setLayoutProperty("snap-preview-point", "visibility", "none");

      map.addSource("snap-preview-line", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });

      map.addLayer({
        id: "snap-preview-line",
        type: "line",
        source: "snap-preview-line",
        paint: {
          "line-color": "#16f2d4",
          "line-width": 3,
          "line-opacity": 0.8,
        },
      });
      map.setLayoutProperty("snap-preview-line", "visibility", "none");

      setMapReady(true);

      map.on("mousemove", async (e) => {
        if (!snapEnabledRef.current) return;
        if (gridModeRef.current !== GRID_MODES.DH) return;

        const draw = drawRef.current;
        if (!draw?.drawing || draw.mode !== "polygon") {
          clearSnapPreview();
          map.getCanvas().style.cursor = "";
          return;
        }

        // throttle to ~15 Hz
        const now = performance.now();
        if (now - snapLastAtRef.current < 66) return;
        snapLastAtRef.current = now;

        // prevent parallel calls
        if (snapInflightRef.current) return;
        snapInflightRef.current = true;

        try {
          const { lng, lat } = e.lngLat;
          const snap = await resolveSnap({ lng, lat });

          const ptSrc = map.getSource("snap-preview");
          if (!ptSrc) return;

          if (!snap) {
            clearSnapPreview();
            map.getCanvas().style.cursor = "crosshair";
            snapCandidateRef.current = null;
            return;
          }

          snapCandidateRef.current = snap;

          ptSrc.setData({
            type: "FeatureCollection",
            features: [{
              type: "Feature",
              geometry: { type: "Point", coordinates: snap.coords },
              properties: {},
            }],
          });

          map.setLayoutProperty("snap-preview-point", "visibility", "visible");
          map.getCanvas().style.cursor = "cell";
        } finally {
          snapInflightRef.current = false;
        }
      });
    };

    whenMapLoaded(map, onLoad);

    const canvas = map.getCanvas();

    function onPointerDownCapture(ev) {

      if (ev.button === 2) return;

      const draw = drawRef.current;
      const snap = snapCandidateRef.current;

      // only when drawing polygon AND we have a snap
      if (!draw?.drawing || draw.mode !== "polygon") return;
      if (!snap?.coords) return;

      // hard-stop the real event (MlDraw/MapLibre must NOT see it)
      ev.preventDefault();
      ev.stopImmediatePropagation();

      const [sx, sy] = snap.coords;

      // build a MapLibre-style event payload
      const lngLat = new maplibregl.LngLat(sx, sy);
      const point = map.project([sx, sy]);

      // Important: fire the same sequence MlDraw expects
      map.fire("mousedown", { lngLat, point, originalEvent: ev });
      map.fire("mouseup", { lngLat, point, originalEvent: ev });
      map.fire("click", { lngLat, point, originalEvent: ev });

      // cleanup
      snapCandidateRef.current = null;
      clearSnapPreview();
    }

    canvas.addEventListener("pointerdown", onPointerDownCapture, true);

    function onPointerUpRestore() {
      // Allow normal interactions again
      snapCandidateRef.current = null;
    }

    canvas.addEventListener("pointerup", onPointerUpRestore);

    return () => {
      try { canvas.removeEventListener("pointerdown", onPointerDownCapture, true); } catch { }
      try { snapAbortRef.current?.abort(); } catch { }

      if (mapRef.current && drawRef.current) {
        mapRef.current.removeControl(drawRef.current);
      }
      mapRef.current?.remove();
      mapRef.current = null;
      drawRef.current = null;
    };

  }, []);

  useEffect(() => {
    const draw = drawRef.current;
    if (!draw) return;

    const feature = draw.lastFeature;
    if (feature && feature.geometry?.type === "Polygon") {
      setHasDraftAoi(true);
      setPendingGeom(feature);
    } else {
      setHasDraftAoi(false);
      setPendingGeom(null);
    }
  }, [drawVersion]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!snapEnabled && map.getSource("snap-preview")) {
      map.getSource("snap-preview").setData({
        type: "FeatureCollection",
        features: [],
      });
    }
  }, [snapEnabled]);

  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    const visible = gridMode === "dh" ? "visible" : "none";

    if (map.getLayer("dh-grid-outline")) {
      map.setLayoutProperty("dh-grid-outline", "visibility", visible);
    }
  }, [gridMode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !selectedId) return;

    let cancelled = false;

    async function render() {
      const baseAoi = aois.find(a => a.id === selectedId);
      if (!baseAoi) return;

      // ---- ZOOM FIRST (bounds always available) ----
      if (Array.isArray(baseAoi.bounds) && baseAoi.bounds.length === 4) {
        map.fitBounds(
          [
            [baseAoi.bounds[0], baseAoi.bounds[1]],
            [baseAoi.bounds[2], baseAoi.bounds[3]],
          ],
          { padding: 40, duration: 500 }
        );
      }

      // ---- RESOLVE GEOMETRY ----
      let geom = baseAoi.geom || geomCache[selectedId];

      if (!geom) {
        try {
          const full = await fetchAoiGeom(selectedId);
          if (cancelled) return;

          geom = full.geom;
          setGeomCache(prev => ({ ...prev, [selectedId]: geom }));
        } catch (e) {
          console.error("Failed to load AOI geometry", e);
          return;
        }
      }

      if (!geom) return;

      const sourceId = "coverage-aoi";

      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, {
          type: "geojson",
          data: {
            type: "Feature",
            geometry: geom,
            properties: {},
          },
        });

        map.addLayer({
          id: "coverage-aoi-fill",
          type: "fill",
          source: sourceId,
          paint: {
            "fill-color": "#f97316",
            "fill-opacity": 0.25,
          },
        });

        map.addLayer({
          id: "coverage-aoi-outline",
          type: "line",
          source: sourceId,
          paint: {
            "line-color": "#fb923c",
            "line-width": 2,
          },
        });
      } else {
        map.getSource(sourceId).setData({
          type: "Feature",
          geometry: geom,
          properties: {},
        });
      }
    }

    render();

    return () => {
      cancelled = true;
    };
  }, [selectedId, aois, mapReady, geomCache]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    if (
      map.getLayer("dh-grid-outline") &&
      map.getLayer("esri-transportation")
    ) {
      map.moveLayer("esri-transportation");
    }

    if (
      map.getLayer("dh-grid-outline") &&
      map.getLayer("esri-boundaries")
    ) {
      map.moveLayer("esri-boundaries");
    }
  }, [mapReady]);

  // ---------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------
  return (
    <div
      className="
        relative
        w-full h-full
        overflow-hidden
        bg-bg
        isolate
      "
    >

      {/* MAP CONTEXT HEADER */}
      <MapContextHeader
        gridMode={gridMode}
        setGridMode={setGridMode}
        snapEnabled={snapEnabled}
        setSnapEnabled={setSnapEnabled}
      />

      {/* Toolbar is UI — must not be part of map event surface */}
      <div className="relative z-20 pointer-events-auto">
        <div className="absolute top-10 left-0">
          <PlatformCoverageAoiToolbar
            draw={drawInstance}
            hasGeometry={hasDraftAoi}
            onSave={saveCoverageAoi}
            onCancel={resetDraftAoi}
          />
        </div>
      </div>

      {hasDraftAoi && pendingGeom && (
        <div className="absolute top-[36px] right-4 z-30 px-3 py-1.5 text-[11px] tracking-wide
                  bg-bg-soft/90 backdrop-blur border border-bg-border rounded-md text-aoi-neon">
          AOI Area: {computeSqMi(pendingGeom).toFixed(2)} sq mi
        </div>
      )}

      {/* Map event surface — strictly confined */}
      <div className="relative w-full h-full">
        <div
          ref={mapContainerRef}
          className="absolute inset-0 w-full h-full"
        />
      </div>
    </div>
  );
}